/**
 * MapLegend: what every color on the capacity map means.
 *
 * A unit's color is an operator choice (Admin > Units > Map colours), not
 * a fixed band, so the map needs a key — this is that key, and it reads
 * the SAME values the pins do (the unit list's colors come from the same
 * careManagers rows CapacityMapPanel already draws the pins from; the
 * three tenant colors come from the same useMapColors() read the map
 * layers use), so the legend can never say something the map itself
 * doesn't.
 *
 * Static, not toggle-coupled: every entry shows regardless of which
 * layers are currently on, so it stays one place a person can check
 * "what does this color mean" without hunting through toggle states.
 *
 * After the units, a four-entry stance key: a care manager's pin is always
 * ringed in her unit's color; its centre is the unit color when she is
 * Open, the Talk to Me gold, the Not Open terracotta, or CM_ON_LEAVE_FILL
 * (grey) on leave. Each swatch is an ink ring around its centre, and
 * Open's centre is ink too, so it reads as "any unit". The key and its
 * divider render only when showStanceKey says the server sent a stance to
 * draw.
 */

import { type ReactNode } from 'react';
import { STANCE_FILL, STATED_AVAILABILITY_LABEL, type StatedAvailability } from '@/lib/capacity';
import { CM_ON_LEAVE_FILL } from '@/lib/mapColors';

export interface MapLegendUnit {
  id: string;
  name: string;
  color: string;
}

export interface MapLegendProps {
  units: MapLegendUnit[];
  /** Whether any care manager on the board has no unit at all — shows the
   *  "Unassigned" entry only when it would mean something. */
  showUnassigned: boolean;
  unassignedColor: string;
  clientColor: string;
  prospectColor: string;
  placementPinColor: string;
  /** Whether the stance key renders: true when any care-manager row the
   *  pins are drawn from carries the stance the server sent. */
  showStanceKey: boolean;
}

function Swatch({ color, hollow = false }: { color: string; hollow?: boolean }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
      style={
        hollow
          ? { border: `1.5px solid ${color}`, backgroundColor: 'transparent' }
          : { backgroundColor: color, border: '1.5px solid #FFFFFF' }
      }
    />
  );
}

/** The stance key, in the toggle's order, then On leave: each entry's
 *  test id suffix and the centre its pin draws (Open's is any unit's
 *  color, drawn in ink). */
const STANCE_KEY: readonly { stance: StatedAvailability; id: string; centre: string }[] = [
  { stance: 'open', id: 'open', centre: 'var(--color-ink)' },
  { stance: 'talk_to_me', id: 'talk-to-me', centre: STANCE_FILL.talk_to_me },
  { stance: 'not_open', id: 'not-open', centre: STANCE_FILL.not_open },
  { stance: 'on_leave', id: 'on-leave', centre: CM_ON_LEAVE_FILL },
];

/** The stance key's swatch: an ink ring around the centre the pin draws. */
function StanceSwatch({ centre }: { centre: string }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-2.5 w-2.5 rounded-full shrink-0 border-2 border-ink"
      style={{ backgroundColor: centre }}
    />
  );
}

/** A thin rule between legend groups. */
function Divider(): ReactNode {
  return <li aria-hidden="true" className="h-3 w-px bg-sage-300/50" />;
}

export function MapLegend({
  units,
  showUnassigned,
  unassignedColor,
  clientColor,
  prospectColor,
  placementPinColor,
  showStanceKey,
}: MapLegendProps): ReactNode {
  return (
    <ul data-testid="map-legend" className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-ink-soft">
      {units.map((u) => (
        <li key={u.id} className="flex items-center gap-1.5" data-testid={`map-legend-unit-${u.id}`}>
          <Swatch color={u.color} />
          {u.name}
        </li>
      ))}
      {showUnassigned && (
        <li className="flex items-center gap-1.5" data-testid="map-legend-unassigned">
          <Swatch color={unassignedColor} />
          Unassigned
        </li>
      )}
      {showStanceKey && (
        <>
          <Divider />
          {STANCE_KEY.map((k) => (
            <li key={k.id} className="flex items-center gap-1.5" data-testid={`map-legend-stance-${k.id}`}>
              <StanceSwatch centre={k.centre} />
              {STATED_AVAILABILITY_LABEL[k.stance]}
            </li>
          ))}
        </>
      )}
      <Divider />
      <li className="flex items-center gap-1.5" data-testid="map-legend-clients">
        <Swatch color={clientColor} />
        Clients
      </li>
      <li className="flex items-center gap-1.5" data-testid="map-legend-prospects">
        <Swatch color={prospectColor} hollow />
        Prospects
      </li>
      <li className="flex items-center gap-1.5" data-testid="map-legend-placement-pin">
        <Swatch color={placementPinColor} />
        Placing a client
      </li>
    </ul>
  );
}
